import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Create axios instance with credentials for httpOnly cookies
const api = axios.create({
  baseURL: API_BASE,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,  // Send cookies with requests
});

// Add auth token to requests as backup (in case cookies don't work)
api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Handle auth errors - but don't redirect for /auth/me failures
api.interceptors.response.use(
  (response) => response,
  (error) => {
    // Don't redirect on 401 from /auth/me (session check) - let AuthContext handle it
    const isAuthMeRequest = error.config?.url?.includes('/auth/me');
    if (error.response?.status === 401 && !isAuthMeRequest) {
      localStorage.removeItem('user');
      localStorage.removeItem('token');
      window.location.href = '/login';
    }
    return Promise.reject(error);
  }
);

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  logout: () => api.post('/auth/logout'),
  getMe: () => api.get('/auth/me'),
  validateSuperuser: (superuser_code) => api.post('/auth/validate-superuser', { superuser_code }),
};

// Usuarios API (gestión de usuarios)
export const usuariosAPI = {
  getAll: () => api.get('/usuarios'),
  getPendientes: () => api.get('/usuarios/pendientes'),
  aprobar: (userId) => api.post(`/usuarios/${userId}/aprobar`),
  rechazar: (userId) => api.delete(`/usuarios/${userId}/rechazar`),
};

// Lotes API
export const lotesAPI = {
  getAll: (estado) => api.get('/lotes', { params: { estado } }),
  getOne: (id) => api.get(`/lotes/${id}`),
  create: (data) => api.post('/lotes', data),
  update: (id, data, superuser_code) => api.put(`/lotes/${id}`, data, { params: { superuser_code } }),
  close: (id, data, superuser_code) => api.post(`/lotes/${id}/cerrar`, data, { params: { superuser_code } }),
  delete: (id, superuser_code) => api.delete(`/lotes/${id}`, { params: { superuser_code } }),
  getComparacionAlimento: (id) => api.get(`/lotes/${id}/comparacion-alimento`),
};

// Lote General API
export const loteGeneralAPI = {
  get: () => api.get('/lote-general'),
  inicializar: () => api.post('/lote-general/inicializar'),
  getGastos: (año, mes) => api.get('/lote-general/gastos', { params: { año, mes } }),
  getResumenMensual: (año) => api.get('/lote-general/resumen-mensual', { params: { año } }),
  distribuir: (año, mes) => api.post('/lote-general/distribuir', { año, mes }),
  revertirDistribucion: (año, mes, superuser_code) => 
    api.delete(`/lote-general/distribucion/${año}/${mes}`, { params: { superuser_code } }),
  getDistribuciones: (año) => api.get('/lote-general/distribuciones', { params: { año } }),
};

// Asignaciones Mensuales API
export const asignacionesAPI = {
  getAll: (año) => api.get('/asignaciones-mensuales', { params: { año } }),
  getMes: (año, mes) => api.get(`/asignaciones-mensuales/${año}/${mes}`),
  guardar: (año, mes, lote_ids) => api.post('/asignaciones-mensuales', { año, mes, lote_ids }),
  eliminar: (año, mes) => api.delete(`/asignaciones-mensuales/${año}/${mes}`),
};

// Expenses API
export const expensesAPI = {
  getAll: (params) => api.get('/expenses', { params }),
  getOne: (id) => api.get(`/expenses/${id}`),
  create: (data) => api.post('/expenses', data),
  update: (id, data, superuser_code) => api.put(`/expenses/${id}`, data, { params: { superuser_code } }),
  delete: (id, superuser_code) => api.delete(`/expenses/${id}`, { params: { superuser_code } }),
};

// Dashboard API
export const dashboardAPI = {
  getStats: () => api.get('/dashboard'),
  getLoteStats: (loteId) => api.get(`/dashboard/lote/${loteId}`),
};

// Helper function for fetch with auth
const fetchWithAuth = (url, options = {}) => {
  const token = localStorage.getItem('token');
  return fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      ...options.headers,
      ...(token ? { Authorization: `Bearer ${token}` } : {})
    }
  });
};

// Reports API (PDF download) - uses credentials + token backup
export const reportsAPI = {
  downloadEstadoResultados: async (loteId, loteName) => {
    const response = await fetchWithAuth(`${API_BASE}/reports/lote/${loteId}/estado-resultados`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `estado_resultados_${loteName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  downloadCategoriaReport: async (loteId, categoria, loteName) => {
    const response = await fetchWithAuth(`${API_BASE}/reports/lote/${loteId}/categoria/${categoria}`);
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `reporte_${categoria}_${loteName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  downloadRecordGranja: async (loteId, loteName) => {
    const response = await fetchWithAuth(`${API_BASE}/reports/farm-record/${loteId}/pdf`);
    if (!response.ok) {
      throw new Error('No existe registro de granja para este lote');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `record_granja_${loteName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  downloadControlAlimento: async (loteId, loteName) => {
    const response = await fetchWithAuth(`${API_BASE}/reports/control-alimento/${loteId}/pdf`);
    if (!response.ok) {
      throw new Error('Error al generar el reporte de control de alimento');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `control_alimento_${loteName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  downloadVentas: async (loteId, loteName) => {
    const response = await fetchWithAuth(`${API_BASE}/reports/ventas/${loteId}/pdf`);
    if (!response.ok) {
      throw new Error('Error al generar el reporte de ventas');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `ventas_${loteName}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  downloadFactura: async (ventaId, noConduce) => {
    const response = await fetchWithAuth(`${API_BASE}/reports/factura/${ventaId}`);
    if (!response.ok) {
      throw new Error('Error al generar la factura');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `factura_${noConduce}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
  downloadEstadoCuenta: async (ventaIds) => {
    const response = await fetchWithAuth(`${API_BASE}/reports/estado-cuenta`, {
      method: 'POST',
      headers: { 
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({ venta_ids: ventaIds })
    });
    if (!response.ok) {
      throw new Error('Error al generar el estado de cuenta');
    }
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `estado_cuenta_${new Date().toISOString().split('T')[0]}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  },
};

// Ventas API
export const ventasAPI = {
  getAll: (params) => api.get('/ventas', { params }),
  getByLote: (loteId) => api.get('/ventas', { params: { lote_id: loteId } }),
  getOne: (id) => api.get(`/ventas/${id}`),
  create: (data) => api.post('/ventas', data),
  update: (id, data, superuser_code) => api.put(`/ventas/${id}`, data, { params: { superuser_code } }),
  delete: (id) => api.delete(`/ventas/${id}`),
  registrarPago: (id, data) => api.post(`/ventas/${id}/pagar`, data),
  editarPago: (ventaId, pagoId, data, superuser_code) => api.put(`/ventas/${ventaId}/pagos/${pagoId}`, data, { params: { superuser_code } }),
  eliminarPago: (ventaId, pagoId, superuser_code) => api.delete(`/ventas/${ventaId}/pagos/${pagoId}`, { params: { superuser_code } }),
  getResumenLote: (loteId) => api.get(`/ventas/lote/${loteId}/resumen`),
};

// Farm Records API
export const farmRecordsAPI = {
  get: (loteId) => api.get(`/farm-records/lote/${loteId}`),
  save: (loteId, data) => api.post(`/farm-records/${loteId}`, data),
};

// Proveedores API
export const proveedoresAPI = {
  getAll: () => api.get('/proveedores'),
  getOne: (id) => api.get(`/proveedores/${id}`),
  create: (data) => api.post('/proveedores', data),
  update: (id, data, superuser_code) => api.put(`/proveedores/${id}`, data, { params: { superuser_code } }),
  delete: (id, superuser_code) => api.delete(`/proveedores/${id}`, { params: { superuser_code } }),
  registrarPago: (id, data) => api.post(`/proveedores/${id}/pagar`, data),
  getPagos: (id) => api.get(`/proveedores/${id}/pagos`),
  editarPago: (proveedorId, pagoId, data, superuser_code) => api.put(`/proveedores/${proveedorId}/pagos/${pagoId}`, data, { params: { superuser_code } }),
  eliminarPago: (proveedorId, pagoId, superuser_code) => api.delete(`/proveedores/${proveedorId}/pagos/${pagoId}`, { params: { superuser_code } }),
  getFacturas: (id) => api.get(`/proveedores/${id}/facturas`),
};

export default api;
